import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Spin } from 'antd';

/**
 * A private route that handles authentication and authorization.
 * @param {array} allowedRoles - Array of role names that are allowed to access the route
 * @param {array} requiredPermissions - Array of permission objects {resource, action, level}
 * @param {string} redirectPath - Path to redirect unauthorized users (default: '/login')
 */
const PrivateRoute = ({ 
  allowedRoles = [], 
  requiredPermissions = [], 
  redirectPath = '/login' 
}) => {
  const {
    isAuthenticated,
    loading: authLoading,
    initialized,
    hasAnyRole,
    hasPermission,
    PERMISSION_LEVELS
  } = useAuth();

  const location = useLocation();

  // Show loading spinner while auth state is being determined
  if (!initialized || authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <Spin size="large" tip="Authenticating..." fullscreen />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // Check role-based access if specified
  const hasRoleAccess = allowedRoles.length === 0 || hasAnyRole(allowedRoles);

  // Check permission-based access if specified
  const hasPermissionAccess = requiredPermissions.length === 0 || 
    requiredPermissions.every(({ resource, action, level }) => 
      hasPermission(resource, action, level || PERMISSION_LEVELS.OWN)
    );

  // Redirect to unauthorized if neither role nor permission requirements are met
  if (!hasRoleAccess && !hasPermissionAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Render child routes if authorized
  return <Outlet />;
};

export default PrivateRoute;